import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Star, Trash2 } from "lucide-react";

import { cafeteriaService, type Feedback, type FoodWaste, type MealOrder, type MenuItem } from "../../services/cafeteriaService";
import { daysFromTodayIso, todayIso } from "../../utils/dates";
import { ClientRegister, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice } from "../register/RecordParts";
import { FeedbackForm, WasteForm } from "./CafeteriaForms";
import { errorOf, useNotice } from "./MenuPanels";
import { RATINGS, WASTE_TYPES, feedbackConfig, feedbackDue, feedbackSummary, labelOf, stars, ugx, wasteConfig, wasteTotals, weekday } from "./cafeteriaLogic";

type PanelProps<T> = { rows?: T[]; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void; initialView?: string };

const Figure = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className="text-xl font-semibold tabular-nums text-gray-900">{value}</p>
    {note && <p className="text-xs text-gray-500">{note}</p>}
  </div>
);

// ---- Feedback -------------------------------------------------------------------------------

export function FeedbackPanel({ rows, kitchen, isLoading, isError, onRetry, onChanged, initialView }: PanelProps<Feedback> & { kitchen: boolean }) {
  const { notice, setNotice, done } = useNotice(onChanged);
  const [viewing, setViewing] = useState<number | null>(null);
  const [rating, setRating] = useState<MealOrder | null>(null);
  // A family rates meals served in the last few weeks.
  const served = useQuery({ queryKey: ["cafeteria", "orders", { from: daysFromTodayIso(-60), to: todayIso() }], queryFn: () => cafeteriaService.getOrdersIn({ from: daysFromTodayIso(-60), to: todayIso() }), enabled: !kitchen });
  const due = useMemo(() => feedbackDue(served.data ?? [], rows ?? []), [served.data, rows]);
  const send = useMutation({ mutationFn: cafeteriaService.createFeedback, onSuccess: (saved) => { setRating(null); served.refetch(); done(`Thank you. Your rating of ${saved.daily_menu_name.toLowerCase()} on ${weekday(saved.menu_date)} has gone to the kitchen.`); } });
  const summary = feedbackSummary(rows ?? []);
  const columns: RegisterColumn<Feedback>[] = [
    { header: "Meal day", cell: (row) => <span className="whitespace-nowrap">{weekday(row.menu_date)}</span>, exportValue: (row) => row.menu_date ?? "" },
    { header: "Meal", cell: (row) => row.daily_menu_name, exportValue: (row) => row.daily_menu_name },
    { header: "Learner", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name },
    ...(kitchen ? [{ header: "Class", cell: (row: Feedback) => row.class_name || "—", exportValue: (row: Feedback) => row.class_name ?? "" }] : []),
    { header: "Overall", cell: (row) => <span className={`whitespace-nowrap ${row.overall_rating <= 2 ? "text-red-700" : "text-amber-600"}`} title={labelOf(RATINGS, row.overall_rating)}>{stars(row.overall_rating)}</span>, exportValue: (row) => row.overall_rating },
    { header: "Taste", cell: (row) => row.taste_rating, exportValue: (row) => row.taste_rating, align: "right" },
    { header: "Quality", cell: (row) => row.quality_rating, exportValue: (row) => row.quality_rating, align: "right" },
    { header: "Service", cell: (row) => row.service_rating, exportValue: (row) => row.service_rating, align: "right" },
    { header: "Comments", cell: (row) => <span className="line-clamp-2 min-w-[12rem] text-gray-700">{row.comments || row.suggestions || "—"}</span>, exportValue: (row) => [row.comments, row.suggestions].filter(Boolean).join(" / ") },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {kitchen && (rows?.length ?? 0) > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Figure label="Overall" value={`${summary.overall.toFixed(1)} / 5`} note={`${summary.count} ratings`} />
          <Figure label="Taste" value={summary.taste.toFixed(1)} />
          <Figure label="Quality and portion" value={summary.quality.toFixed(1)} />
          <Figure label="Service" value={summary.service.toFixed(1)} />
          <Figure label="Would recommend" value={`${summary.recommend}%`} />
        </div>
      )}
      {!kitchen && (
        <section className="card">
          <div className="card-header pb-3"><h3 className="font-semibold text-gray-900">Waiting for your rating</h3><p className="mt-1 text-sm text-gray-500">Meals served to your children in the last 60 days that you have not rated.</p></div>
          <div className="card-content">
            {served.isLoading ? <p className="text-sm text-gray-500">Loading…</p> : due.length ? (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {due.map((order) => (
                  <li key={order.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span><span className="font-medium text-gray-900">{order.daily_menu_name}, {weekday(order.menu_date)}</span> <span className="text-gray-500">· {order.student_name}</span></span>
                    <button type="button" className="btn btn-outline h-8 gap-1.5 text-xs" onClick={() => { send.reset(); setRating(order); }}><Star className="h-3.5 w-3.5" /> Rate it</button>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-gray-500">Nothing to rate. Meals appear here once they are served.</p>}
          </div>
        </section>
      )}
      <ClientRegister
        key={initialView}
        title={kitchen ? "Feedback" : "Your ratings"}
        description={kitchen ? "What learners and families said about meals they were served, lowest first when you need to act on it." : "Ratings you have sent the kitchen."}
        rows={rows}
        config={feedbackConfig(kitchen)}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["rating", "ratings"]}
        exportName={{ file: "cafeteria-feedback", title: "Cafeteria Feedback" }}
        canExport={kitchen}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No ratings yet."
      />
      {row && (
        <DetailsModal
          title={row.student_name}
          headline={{ label: `${row.daily_menu_name} · ${weekday(row.menu_date)}`, value: `${row.overall_rating} / 5`, note: labelOf(RATINGS, row.overall_rating) }}
          facts={[["Taste", `${row.taste_rating} · ${labelOf(RATINGS, row.taste_rating)}`], ["Quality and portion", `${row.quality_rating} · ${labelOf(RATINGS, row.quality_rating)}`], ["Service", `${row.service_rating} · ${labelOf(RATINGS, row.service_rating)}`], ["Would recommend", row.would_recommend ? "Yes" : "No"], ["Would order again", row.would_order_again ? "Yes" : "No"], ...(kitchen ? [["Class", row.class_name || "—"] as [string, string]] : [])]}
          sections={[{ title: "Comments", body: row.comments }, { title: "Suggestions", body: row.suggestions }]}
          onClose={() => setViewing(null)}
        />
      )}
      {rating && <FeedbackForm order={rating} busy={send.isPending} error={errorOf(send, "The rating could not be sent.")} onSave={(data) => send.mutate(data)} onClose={() => setRating(null)} />}
    </div>
  );
}

// ---- Waste ----------------------------------------------------------------------------------

export function WastePanel({ rows, items, isLoading, isError, onRetry, onChanged }: PanelProps<FoodWaste> & { items: MenuItem[] }) {
  const { notice, setNotice, done } = useNotice(onChanged);
  const [viewing, setViewing] = useState<number | null>(null);
  const [form, setForm] = useState(false);
  const record = useMutation({ mutationFn: cafeteriaService.createWaste, onSuccess: (saved) => { setForm(false); done(`${saved.quantity} ${saved.unit} of ${labelOf(WASTE_TYPES, saved.waste_type).toLowerCase()} recorded.`); } });
  const month = todayIso().slice(0, 7);
  const totals = wasteTotals(rows ?? [], month);
  const columns: RegisterColumn<FoodWaste>[] = [
    { header: "Date", cell: (row) => <span className="whitespace-nowrap">{weekday(row.date)}</span>, exportValue: (row) => row.date },
    { header: "Kind", cell: (row) => labelOf(WASTE_TYPES, row.waste_type), exportValue: (row) => labelOf(WASTE_TYPES, row.waste_type) },
    { header: "Dish", cell: (row) => row.menu_item_name || "General", exportValue: (row) => row.menu_item_name || "General" },
    { header: "Quantity", cell: (row) => <span className="tabular-nums">{Number(row.quantity).toLocaleString()} {row.unit}</span>, exportValue: (row) => `${row.quantity} ${row.unit}`, align: "right" },
    { header: "Cost", cell: (row) => (row.cost_impact ? <span className="tabular-nums">{Number(row.cost_impact).toLocaleString()}</span> : "—"), exportValue: (row) => (row.cost_impact ? Number(row.cost_impact) : ""), align: "right" },
    { header: "Why", cell: (row) => <span className="line-clamp-2 min-w-[12rem] text-gray-700">{row.reason || "—"}</span>, exportValue: (row) => row.reason ?? "" },
    { header: "What will change", cell: (row) => row.action_taken || "", exportValue: (row) => row.action_taken ?? "", exportOnly: true },
    { header: "Recorded by", cell: (row) => row.recorded_by_name || "", exportValue: (row) => row.recorded_by_name ?? "", exportOnly: true },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="grid gap-3 sm:grid-cols-3">
        <Figure label="Wasted this month" value={ugx(totals.cost)} note={`${totals.records} ${totals.records === 1 ? "record" : "records"}`} />
        <Figure label="Weighed this month" value={`${totals.kg.toLocaleString()} kg`} note="Records kept in kg" />
        <Figure label="Costliest kind" value={totals.worst ? labelOf(WASTE_TYPES, totals.worst) : "—"} note="By cost, this month" />
      </div>
      <ClientRegister
        title="Food waste"
        description="What the kitchen threw away, why, and what will change. Records cannot be edited once saved."
        actions={<button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { record.reset(); setForm(true); }}><Trash2 className="h-4 w-4" /> Record waste</button>}
        rows={rows}
        config={wasteConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["record", "records"]}
        exportName={{ file: "cafeteria-food-waste", title: "Cafeteria Food Waste" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No waste recorded."
      />
      {row && (
        <DetailsModal
          title={`${labelOf(WASTE_TYPES, row.waste_type)}, ${weekday(row.date)}`}
          headline={{ label: row.menu_item_name || "General kitchen waste", value: `${Number(row.quantity).toLocaleString()} ${row.unit}`, note: row.cost_impact ? `Cost ${ugx(row.cost_impact)}` : "No cost entered" }}
          facts={[["Recorded by", row.recorded_by_name || "—"], ["Recorded on", row.created_at?.slice(0, 10) ?? "—"]]}
          sections={[{ title: "Why it was wasted", body: row.reason }, { title: "What will change", body: row.action_taken }]}
          onClose={() => setViewing(null)}
        />
      )}
      {form && <WasteForm items={items} busy={record.isPending} error={errorOf(record, "The waste could not be recorded.")} onSave={(data) => record.mutate(data)} onClose={() => setForm(false)} />}
    </div>
  );
}
